import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AgentCitation } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { DownloadMenu } from '../components/DownloadMenu.js';
import { apiDelete, apiGet, apiPost, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import {
  BrainIcon,
  CableIcon,
  ExternalLinkIcon,
  PlusIcon,
  SendIcon,
  Trash2Icon,
} from '../components/icons.js';

/**
 * My Agent — personal assistant over the user's own M365/Jira data blended
 * with the org knowledgebase (2026-06-09 design, slice 1; live-first v1).
 *
 * Deliberately lean compared to the Knowledge Agent page: no scope picker,
 * answer modes, attachments, canvas or model picker. Non-streaming v1.
 */

interface Citation {
  chunkId: string;
  title?: string;
  snippet?: string;
  ring?: string;
  sourceUrl?: string;
}

interface Turn {
  question: string;
  answer: string;
  citations: Citation[];
}

interface ChatRow {
  chatId: string;
  title?: string;
  lastQuestion?: string;
  messageCount?: number;
  updatedAt?: string;
}

interface ChatAnswer {
  requestId: string;
  chatId: string;
  answer: string;
  citations: Citation[];
  outcome: string;
}

/** Replace `[chunkId: a, b]` markers with numbered superscripts wired to the
 *  citation list below the answer. */
function renderableAnswer(answer: string, citations: Citation[]): string {
  const order = new Map(citations.map((c, i) => [c.chunkId, i + 1]));
  return answer.replace(/\[chunkId:\s*([^\]]+)\]/g, (_m, ids: string) => {
    const nums = ids
      .split(',')
      .map((s: string) => order.get(s.trim()))
      .filter((n): n is number => typeof n === 'number');
    return nums.length ? ` ${nums.map((n) => `[${n}]`).join('')}` : '';
  });
}

const VALID_RINGS = ['ring0', 'ring1', 'ring2', 'tool'] as const;

/** Adapt My Agent's local Citation to the shared AgentCitation the (reused)
 *  DownloadMenu expects — fill the fields it requires (title/snippet/ring). */
function toAgentCitations(citations: Citation[]): AgentCitation[] {
  return citations.map((c) => ({
    chunkId: c.chunkId,
    title: c.title ?? c.chunkId,
    snippet: c.snippet ?? '',
    ring: (VALID_RINGS as readonly string[]).includes(c.ring ?? '')
      ? (c.ring as AgentCitation['ring'])
      : 'ring1',
    ...(c.sourceUrl ? { sourceUrl: c.sourceUrl } : {}),
  }));
}

function CitationCard({ citation, index }: { citation: Citation; index: number }): JSX.Element {
  const isTool = citation.ring === 'tool';
  return (
    <div className="flex items-start gap-2 rounded-md border border-nebula-border-soft bg-white px-3 py-2">
      <span className="mt-0.5 shrink-0 font-mono text-[11px] text-nebula-fg-3">[{index + 1}]</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
              isTool ? 'bg-slate-200 text-slate-700' : 'bg-nebula-tint text-nebula-secondary'
            }`}
          >
            {isTool ? 'Live' : 'Knowledgebase'}
          </span>
          <span className="ov-body truncate font-medium">{citation.title ?? citation.chunkId}</span>
        </div>
        {citation.snippet && (
          <p className="ov-meta mt-0.5 line-clamp-2 text-nebula-fg-2">{citation.snippet}</p>
        )}
        {citation.sourceUrl && (
          <a
            href={citation.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="ov-meta mt-0.5 inline-flex items-center gap-1 text-nebula-secondary hover:underline"
          >
            Open source
            <ExternalLinkIcon className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

const SUGGESTIONS = [
  'What does my day look like?',
  'Prep me for my next 1:1',
  'Any unread mail I should look at?',
  'Which Jira tickets are assigned to me?',
];

export function MyAgentPage(): JSX.Element {
  const { user, flags } = useAuth();
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsEntra, setNeedsEntra] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const loadChats = useCallback((): void => {
    apiGet<{ chats: ChatRow[] }>('/api/v1/my-agent/chats')
      .then((r) => setChats(r.chats))
      .catch(() => setChats([]));
  }, []);

  useEffect(loadChats, [loadChats]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, pending]);

  const openChat = (chatId: string): void => {
    setError(null);
    apiGet<{
      chat: ChatRow;
      messages: Array<{ role: string; content: string; sources?: Citation[] }>;
    }>(`/api/v1/my-agent/chats/${chatId}`)
      .then((r) => {
        const loaded: Turn[] = [];
        for (let i = 0; i < r.messages.length; i += 1) {
          const m = r.messages[i]!;
          if (m.role !== 'user') continue;
          const next = r.messages[i + 1];
          loaded.push({
            question: m.content,
            answer: next?.role === 'assistant' ? next.content : '',
            citations: next?.sources ?? [],
          });
        }
        setTurns(loaded);
        setCurrentChatId(chatId);
      })
      .catch(() => setError('Could not load that conversation.'));
  };

  const newChat = (): void => {
    setCurrentChatId(null);
    setTurns([]);
    setDraft('');
    setError(null);
  };

  const removeChat = (chatId: string): void => {
    // Optimistic: drop the row locally (the id is known) instead of a full
    // 50-row list refetch. Only clear the open conversation AFTER the delete
    // succeeds — a failed delete restores the rail and leaves the panel
    // intact rather than blanking a conversation that still exists.
    setChats((prev) => prev.filter((c) => c.chatId !== chatId));
    void apiDelete(`/api/v1/my-agent/chats/${chatId}`)
      .then(() => {
        if (chatId === currentChatId) newChat();
      })
      .catch(() => loadChats());
  };

  const submit = (question: string): void => {
    const query = question.trim();
    if (!query || pending) return;
    setPending(true);
    setError(null);
    setDraft('');
    setTurns((t) => [...t, { question: query, answer: '', citations: [] }]);
    apiPost<ChatAnswer>('/api/v1/my-agent/chat', {
      query,
      ...(currentChatId ? { chatId: currentChatId } : {}),
    })
      .then((r) => {
        const isNewChat = !currentChatId;
        setCurrentChatId(r.chatId);
        setTurns((t) => {
          const copy = [...t];
          copy[copy.length - 1] = { question: query, answer: r.answer, citations: r.citations };
          return copy;
        });
        // A brand-new chat needs to appear in the rail (with its generated
        // title) — refetch once. For an existing chat, update the row in
        // place rather than refetching all 50 summaries every turn.
        if (isNewChat) {
          loadChats();
        } else {
          setChats((prev) =>
            prev.map((c) =>
              c.chatId === r.chatId
                ? { ...c, lastQuestion: query, messageCount: (c.messageCount ?? 0) + 2 }
                : c,
            ),
          );
        }
      })
      .catch((err: unknown) => {
        setTurns((t) => t.slice(0, -1));
        if (err instanceof ApiError && err.code === 'my_agent_requires_entra') {
          setNeedsEntra(true);
        } else if (err instanceof ApiError && err.status === 429) {
          setError('You have asked a lot in a row — give it a minute.');
        } else {
          setError('My Agent could not answer that. Try again in a moment.');
        }
      })
      .finally(() => setPending(false));
  };

  if (!user) return <></>;
  // Phased rollout (slice 6): the backend enforces this on every route; the
  // redirect just keeps deep links from rendering a dead page.
  if (!flags.myAgentEnabled) return <Navigate to="/" replace />;

  return (
    <Layout>
      {/* One page-level scrollbar (the Layout <main> owns the scroll) rather
          than a nested scroll region on just the message list. The rail and
          the input dock stay put via `sticky` as the whole page scrolls. */}
      <div className="flex min-h-[calc(100vh-3.5rem)]">
        {/* ── Conversations rail ─────────────────────────────────── */}
        <aside className="sticky top-0 hidden h-[calc(100vh-3.5rem)] w-64 shrink-0 flex-col self-start border-r border-nebula-border-soft bg-nebula-surface md:flex">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="ov-meta-upper text-nebula-fg-3">Conversations</span>
            <button
              type="button"
              onClick={newChat}
              title="New conversation"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-nebula-fg-2 hover:bg-nebula-tint"
            >
              <PlusIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
            {chats.length === 0 && (
              <p className="ov-meta px-2 text-nebula-fg-3">No conversations yet.</p>
            )}
            {chats.map((c) => (
              <div
                key={c.chatId}
                className={`group mb-1 flex items-start gap-1 rounded-md px-2 py-1.5 ${
                  c.chatId === currentChatId ? 'bg-nebula-tint' : 'hover:bg-nebula-tint/60'
                }`}
              >
                <button
                  type="button"
                  onClick={() => openChat(c.chatId)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="ov-body block truncate text-[13px] font-medium text-nebula-fg-1">
                    {c.title && c.title !== 'New conversation' ? c.title : (c.lastQuestion ?? '…')}
                  </span>
                  <span className="ov-meta block truncate text-nebula-fg-3">
                    {c.messageCount ?? 0} messages
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => removeChat(c.chatId)}
                  title="Delete conversation"
                  className="invisible mt-0.5 shrink-0 rounded p-1 text-nebula-fg-4 hover:text-nebula-error group-hover:visible"
                >
                  <Trash2Icon className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* ── Chat column ────────────────────────────────────────── */}
        <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4">
          <div className="flex-1 py-6">
            {turns.length === 0 && !pending && (
              <div className="mt-10 text-center">
                <BrainIcon className="mx-auto h-10 w-10 text-nebula-secondary" />
                <h1 className="ov-h-page mt-3">My Agent</h1>
                <p className="ov-body mx-auto mt-2 max-w-md text-nebula-fg-2">
                  Your personal assistant over your own mail, calendar, Teams chats, files and
                  tickets — blended with the OneNebula knowledgebase. Only you can see these
                  answers.
                </p>
                <div className="mx-auto mt-6 grid max-w-lg grid-cols-1 gap-2 sm:grid-cols-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => submit(s)}
                      className="rounded-md border border-nebula-border-soft bg-white px-3 py-2 text-left font-ui text-sm text-nebula-fg-1 hover:bg-nebula-tint"
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <p className="ov-meta mt-6 text-nebula-fg-3">
                  Uses your own connections — manage them under{' '}
                  <Link
                    to="/settings/connections"
                    className="text-nebula-secondary hover:underline"
                  >
                    Settings → Connections
                  </Link>
                  .
                </p>
              </div>
            )}

            {turns.map((t, i) => (
              <div key={i} className="mb-6">
                <div className="ml-auto w-fit max-w-[85%] rounded-2xl bg-nebula-primary px-4 py-2.5 text-sm text-white">
                  {t.question}
                </div>
                {t.answer ? (
                  <div className="mt-3 rounded-2xl border border-nebula-border-soft bg-white px-5 py-4 shadow-card">
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {renderableAnswer(t.answer, t.citations)}
                      </ReactMarkdown>
                    </div>
                    {t.citations.length > 0 && (
                      <div className="mt-3 space-y-1.5 border-t border-nebula-border-soft pt-3">
                        {t.citations.map((c, ci) => (
                          <CitationCard key={c.chunkId} citation={c} index={ci} />
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex justify-end border-t border-nebula-border-soft pt-3">
                      <DownloadMenu
                        question={t.question}
                        answer={t.answer}
                        citations={toAgentCitations(t.citations)}
                      />
                    </div>
                  </div>
                ) : (
                  pending &&
                  i === turns.length - 1 && (
                    <div className="mt-3 rounded-2xl border border-nebula-border-soft bg-white px-5 py-4 shadow-card">
                      <p className="ov-body animate-pulse text-nebula-fg-3">
                        Checking your sources…
                      </p>
                    </div>
                  )
                )}
              </div>
            ))}

            {error && (
              <div className="rounded-md border border-nebula-border-soft bg-nebula-error-bg px-4 py-3">
                <p className="ov-body text-nebula-error">{error}</p>
              </div>
            )}

            {needsEntra && (
              <div className="flex items-start gap-3 rounded-md border border-nebula-border-soft bg-nebula-tint px-4 py-3">
                <CableIcon className="mt-0.5 h-4 w-4 shrink-0 text-nebula-secondary" />
                <p className="ov-body text-nebula-fg-1">
                  My Agent needs your work sign-in to read your own data. Sign out and sign in with
                  your OneNebula account, then check{' '}
                  <Link to="/settings/connections" className="text-nebula-secondary underline">
                    your connections
                  </Link>
                  .
                </p>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* ── Input ─────────────────────────────────────────────── */}
          <form
            className="sticky bottom-0 border-t border-nebula-border-soft bg-nebula-app pb-4 pt-3"
            onSubmit={(e) => {
              e.preventDefault();
              submit(draft);
            }}
          >
            <div className="flex items-end gap-2 rounded-2xl border border-nebula-border bg-white px-3 py-2 shadow-card">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submit(draft);
                  }
                }}
                rows={1}
                placeholder="Ask about your day, mail, chats, files or tickets…"
                className="max-h-32 min-h-[2rem] flex-1 resize-none bg-transparent py-1 font-ui text-sm text-nebula-fg-1 outline-none placeholder:text-nebula-fg-4"
              />
              <button
                type="submit"
                disabled={pending || !draft.trim()}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-nebula-secondary text-white disabled:opacity-40"
                title="Send"
              >
                <SendIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="ov-meta mt-1.5 text-center text-nebula-fg-4">
              Read-only: My Agent never sends mail or changes anything on your behalf.
            </p>
          </form>
        </div>
      </div>
    </Layout>
  );
}
